import { Group, List, Stack, ThemeIcon, Title, Tooltip } from "@mantine/core"
import { IconCircleCheck, IconCircleX } from "@tabler/icons-react"
import { CertificateCategory, CertificateStatus } from "@tfg/db"
import { Card } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { CustomDrawer } from "~/components/CustomDrawer.tsx"
import { FindProductsProductStandardDto } from "~/gen/api"
import { CertificateSummary } from "~/pages/Brand/Certificates/components/CertificateSummary.tsx"
import { standardRulesTranslations } from "~/pages/Brand/Certificates/type.ts"
import {
  getComplianceRules,
  standardRequiresDeepCompliance,
} from "~/pages/Brand/Products/List/Services/standardCompliance.tsx"

type Props = {
  standard: FindProductsProductStandardDto
  onClose: () => void
}

export const StandardComplianceDrawer = ({ standard, onClose }: Props) => {
  return (
    <CustomDrawer size="lg" onClose={onClose} opened={true}>
      <Group
        mih="100%"
        bg="var(--mantine-color-brand-background)"
        align="start"
      >
        <Stack w="100%" p="xl" justify="start" align="start">
          <Title order={2}>{standard.name}</Title>
          {standardRequiresDeepCompliance(standard) ? (
            <ComplianceStandardView standard={standard} />
          ) : (
            <SimpleStandardView standard={standard} />
          )}
        </Stack>
      </Group>
    </CustomDrawer>
  )
}

const SimpleStandardView = ({
  standard,
}: {
  standard: FindProductsProductStandardDto
}) => {
  const { t } = useTranslation("certificates")

  const totalCertificates = standard.certificates.length
  const completedCertificates = standard.certificates.filter(
    (certificate) => certificate.status === CertificateStatus.PROCESSED
  ).length

  return (
    <>
      <Title order={6}>
        {t("standard.drawer.certificatesNumber", {
          completedCertificates,
          totalCertificates,
        })}
      </Title>
      {standard.certificates.map((certificate, index) => (
        <Stack
          bg="var(--mantine-color-brand-background)"
          h="100%"
          p="md"
          justify="start"
          align="start"
          key={index}
        >
          <Card style={{ width: "100%" }}>
            <Stack>
              <CertificateSummary
                showDocumentIcons={true}
                certificate={certificate}
                maxListElements={5}
              />
            </Stack>
          </Card>
        </Stack>
      ))}
    </>
  )
}

const CompliantIcon = () => (
  <ThemeIcon color="primary" size={24} radius="xl">
    <IconCircleCheck style={{ width: "16px", height: "16px" }} />
  </ThemeIcon>
)

const NotCompliantIcon = () => (
  <ThemeIcon color="#E0525B" size={24} radius="xl">
    <IconCircleX style={{ width: "16px", height: "16px" }} />
  </ThemeIcon>
)

const ComplianceStandardView = ({
  standard,
}: {
  standard: FindProductsProductStandardDto
}) => {
  const { t } = useTranslation("certificates")

  const transactionCertificate = standard.certificates.find(
    (certificate) =>
      certificate.type.category === CertificateCategory.TRANSACTION &&
      certificate.status === CertificateStatus.PROCESSED
  )
  const scopeCertificate = standard.certificates.find(
    (certificate) =>
      certificate.type.category === CertificateCategory.SCOPE &&
      certificate.status === CertificateStatus.PROCESSED
  )

  const hasMissingCertificates = !scopeCertificate || !transactionCertificate

  return (
    <>
      {standard.certificates.map((certificate, index) => (
        <Stack
          bg="var(--mantine-color-brand-background)"
          h="100%"
          p="md"
          justify="start"
          align="start"
          key={index}
        >
          <Card style={{ width: "100%" }}>
            <Stack>
              <CertificateSummary
                showDocumentIcons={true}
                certificate={certificate}
                maxListElements={5}
              />
            </Stack>
          </Card>
        </Stack>
      ))}
      {!hasMissingCertificates ? (
        <>
          <Title order={4}>{t("standard.drawer.complianceRules")}</Title>
          <List ml="xl" spacing="lg" size="md" center>
            {getComplianceRules(transactionCertificate, scopeCertificate).map(
              (rule, index) => {
                const icon = rule.isCompliant ? (
                  <CompliantIcon />
                ) : (
                  <NotCompliantIcon />
                )

                return (
                  <Tooltip
                    key={index}
                    color="white"
                    c="black"
                    position="top-start"
                    offset={5}
                    label={t(standardRulesTranslations[rule.description])}
                    multiline
                    maw={400}
                  >
                    <List.Item icon={icon}>
                      {t(standardRulesTranslations[rule.name])}
                    </List.Item>
                  </Tooltip>
                )
              }
            )}
          </List>
        </>
      ) : null}
    </>
  )
}
